import { api } from './api';

// ---------- Admin endpoints ----------

export const adminApi = {
  // Response Scale Library (Scale Manager UI)
  listResponseScales: (includeInactive = false) =>
    api.get('/admin/library/response-scales', { params: { include_inactive: includeInactive } }).then((r) => r.data),
  responseScalesUsage: () => api.get('/admin/library/response-scales/usage').then((r) => r.data),
  createResponseScale: (body) => api.post('/admin/library/response-scales', body).then((r) => r.data),
  updateResponseScale: (id, body) => api.patch(`/admin/library/response-scales/${id}`, body).then((r) => r.data),
  newResponseScaleVersion: (id, body) => api.post(`/admin/library/response-scales/${id}/new-version`, body).then((r) => r.data),
  duplicateResponseScale: (id) => api.post(`/admin/library/response-scales/${id}/duplicate`).then((r) => r.data),
  archiveResponseScale: (id) => api.delete(`/admin/library/response-scales/${id}`).then((r) => r.data),
  reactivateResponseScale: (id) => api.post(`/admin/library/response-scales/${id}/reactivate`).then((r) => r.data),

  // Competencies
  listCompetencies: (includeArchived = false) =>
    api.get('/admin/competencies', { params: { include_archived: includeArchived } }).then((r) => r.data),
  createCompetency: (body) => api.post('/admin/competencies', body).then((r) => r.data),
  competencyImportTemplate: () => api.get('/admin/competencies/import/template.xlsx', { responseType: 'blob' }),
  competencyImportPreview: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/admin/competencies/import/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },
  competencyImportCommit: (rows) => api.post('/admin/competencies/import/commit', { rows }).then((r) => r.data),
  updateCompetency: (id, body) => api.patch(`/admin/competencies/${id}`, body).then((r) => r.data),
  exportCompetencies: () => api.get('/admin/competencies/export.xlsx', { responseType: 'blob' }),
  competencyUsage: (id) => api.get(`/admin/competencies/${id}/usage`).then((r) => r.data),

  // Role profiles
  listRoleProfiles: (includeArchived = false, companyId = null) =>
    api.get('/admin/role_profiles', {
      params: { include_archived: includeArchived, ...(companyId ? { company_id: companyId } : {}) },
    }).then((r) => r.data),
  getRoleProfile: (id) => api.get(`/admin/role_profiles/${id}`).then((r) => r.data),
  createRoleProfile: (body, autoCode = false) =>
    api.post('/admin/role_profiles', body, { params: { auto_code: autoCode } }).then((r) => r.data),
  updateRoleProfile: (id, body) => api.patch(`/admin/role_profiles/${id}`, body).then((r) => r.data),
  forkRoleProfile: (id, overrides) =>
    api.post(`/admin/role_profiles/${id}/fork`, overrides).then((r) => r.data),
  // Phase 3 — same-tenant duplication + governed global promotion
  duplicateRoleProfile: (id, body) =>
    api.post(`/admin/role_profiles/${id}/duplicate`, body).then((r) => r.data),
  promoteRoleProfile: (id, body) =>
    api.post(`/admin/role_profiles/${id}/promote`, body).then((r) => r.data),

  // Phase 3 — Core Positions (Person → Position → Role Profile)
  listPositions: (companyId, departmentId = null) =>
    api.get('/admin/positions', {
      params: { company_id: companyId, ...(departmentId ? { department_id: departmentId } : {}) },
    }).then((r) => r.data),
  createPosition: (body) => api.post('/admin/positions', body).then((r) => r.data),
  updatePosition: (id, body) => api.patch(`/admin/positions/${id}`, body).then((r) => r.data),
  assignPositionOccupant: (id, userId) =>
    api.post(`/admin/positions/${id}/occupants`, { user_id: userId }).then((r) => r.data),
  removePositionOccupant: (id, userId) =>
    api.delete(`/admin/positions/${id}/occupants/${userId}`).then((r) => r.data),
  getPositionHistory: (id) => api.get(`/admin/positions/${id}/history`).then((r) => r.data),

  // Questionnaires
  listQuestionnaires: () => api.get('/admin/questionnaires').then((r) => r.data),
  listQuestionnairesPaged: (params) => api.get('/admin/questionnaires', { params }).then((r) => r.data),
  resolveEmployees: (ids) => api.get('/admin/employees/resolve', { params: { ids: ids.join(',') } }).then((r) => r.data),
  getQuestionnaire: (id) => api.get(`/admin/questionnaires/${id}`).then((r) => r.data),
  createQuestionnaire: (body) => api.post('/admin/questionnaires', body).then((r) => r.data),
  updateQuestionnaireMeta: (id, body) => api.put(`/admin/questionnaires/${id}`, body).then((r) => r.data),
  createDraftVersion: (id, body) =>
    api.post(`/admin/questionnaires/${id}/versions`, body).then((r) => r.data),
  publishVersion: (qid, vid) =>
    api.post(`/admin/questionnaires/${qid}/versions/${vid}/publish`).then((r) => r.data),
  archiveQuestionnaire: (id) => api.post(`/admin/questionnaires/${id}/archive`).then((r) => r.data),
  // Library MVP — 4 lifecycle transitions.
  submitEvaluationForReview: (qid) =>
    api.post(`/admin/questionnaires/${qid}/submit-for-review`).then((r) => r.data),
  requestEvaluationChanges: (qid, reason) =>
    api.post(`/admin/questionnaires/${qid}/request-changes`, { reason }).then((r) => r.data),
  approveEvaluation: (qid) =>
    api.post(`/admin/questionnaires/${qid}/approve`).then((r) => r.data),
  publishEvaluation: (qid) =>
    api.post(`/admin/questionnaires/${qid}/publish-evaluation`).then((r) => r.data),
  // Sprint A — Duplicate evaluation.
  duplicateEvaluation: (qid) =>
    api.post(`/admin/questionnaires/${qid}/duplicate`).then((r) => r.data),
  // Sprint A — Library vocab (cached at the call-site).
  getLibraryVocab: (params = {}) =>
    api.get('/admin/library/vocab', { params }).then((r) => r.data),
  // Library V2 Phase 3 — search-first Library
  librarySearch: (params = {}) =>
    api.get('/admin/library/search', { params }).then((r) => r.data),
  // Library V2 Phase 4 — Assessment Profile + governed configurations
  getAssessmentProfile: (aid) =>
    api.get(`/admin/library/assessments/${aid}/profile`).then((r) => r.data),
  listAssessmentConfigs: (aid) =>
    api.get(`/admin/library/assessments/${aid}/configurations`).then((r) => r.data),
  createAssessmentConfig: (aid, payload) =>
    api.post(`/admin/library/assessments/${aid}/configurations`, payload).then((r) => r.data),
  updateAssessmentConfig: (cid, patch) =>
    api.patch(`/admin/library/configurations/${cid}`, patch).then((r) => r.data),
  transitionAssessmentConfig: (cid, action, reason = null) =>
    api.post(`/admin/library/configurations/${cid}/transition`, { action, reason }).then((r) => r.data),
  setDefaultAssessmentConfig: (cid) =>
    api.post(`/admin/library/configurations/${cid}/set_default`).then((r) => r.data),
  libraryTaxonomy: (params = {}) =>
    api.get('/admin/library/taxonomy', { params }).then((r) => r.data),
  libraryRecommendedUses: () =>
    api.get('/admin/library/recommended-uses').then((r) => r.data),
  // Sprint A.1.1 — DB-backed catalog CRUD.
  createVocabValue: (field, payload) =>
    api.post(`/admin/library/vocab/${field}`, payload).then((r) => r.data),
  updateVocabValue: (field, code, patch) =>
    api.put(`/admin/library/vocab/${field}/${code}`, patch).then((r) => r.data),
  // Sprint A.1.1 — Published → Editable.
  revertEvaluationToApproved: (qid) =>
    api.post(`/admin/questionnaires/${qid}/revert-to-approved`).then((r) => r.data),
  revertEvaluationToDraft: (qid) =>
    api.post(`/admin/questionnaires/${qid}/revert-to-draft`).then((r) => r.data),
  // Sprint A.1.1 — Import wizard.
  importTemplateUrl: (format = 'xlsx') =>
    `${api.defaults.baseURL}/admin/library/import/template?format=${format}`,
  importPreview: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/admin/library/import/preview', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },
  importConfirm: (preview) =>
    api.post('/admin/library/import/confirm', preview).then((r) => r.data),

  // Sprint A.1.3-α — Resource Center.
  resourcesMeta: () =>
    api.get('/admin/resources/meta').then((r) => r.data),
  listResources: (params = {}) =>
    api.get('/admin/resources', { params }).then((r) => r.data),
  getResource: (rid) =>
    api.get(`/admin/resources/${rid}`).then((r) => r.data),
  createResource: (payload) =>
    api.post('/admin/resources', payload).then((r) => r.data),
  updateResource: (rid, patch) =>
    api.put(`/admin/resources/${rid}`, patch).then((r) => r.data),
  uploadResourceFile: (rid, file, { version, notes } = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    if (version) fd.append('version', version);
    if (notes) fd.append('notes', notes);
    return api.post(`/admin/resources/${rid}/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },
  publishResource: (rid) =>
    api.post(`/admin/resources/${rid}/publish`).then((r) => r.data),
  deprecateResource: (rid) =>
    api.post(`/admin/resources/${rid}/deprecate`).then((r) => r.data),
  archiveResource: (rid) =>
    api.post(`/admin/resources/${rid}/archive`).then((r) => r.data),
  downloadResource: (rid) =>
    api.get(`/admin/resources/${rid}/download`, { responseType: 'blob' }).then((r) => r),

  // Sprint A.1.3-β — Preview / Export / Import / Versions.
  previewResource: (rid, lang = 'en') =>
    api.get(`/admin/resources/${rid}/preview`, { params: { lang } }).then((r) => r.data),
  exportResource: (rid, format = 'pdf', lang = 'en') =>
    api.get(`/admin/resources/${rid}/export`, {
      params: { format, lang },
      responseType: 'blob',
    }).then((r) => r),
  importResourceToLibrary: (rid) =>
    api.post(`/admin/resources/${rid}/import-to-library`).then((r) => r.data),
  listResourceVersions: (rid) =>
    api.get(`/admin/resources/${rid}/versions`).then((r) => r.data),
  snapshotResource: (rid) =>
    api.post(`/admin/resources/${rid}/snapshot`).then((r) => r.data),

  // Sprint A.1.3-γ — Validation Center / Import Summary / Analytics.
  validateImport: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/admin/library/import/validate', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },
  exportImportSummaryPdf: (summary) =>
    api.post('/admin/library/import/summary/pdf', summary, { responseType: 'blob' }).then((r) => r),
  resourceAnalyticsOverview: () =>
    api.get('/admin/resources/analytics/overview').then((r) => r.data),

  // Suites + competencies catalog (read-only)
  // Suites ship as part of the seed; we don't allow create/update yet from the UI.

  // Reports
  listReports: (status = null, opts = {}) => {
    const params = {};
    if (status) params.status = status;
    if (opts.book === 'all') params.book = 'all';
    return api.get('/admin/reports', { params }).then((r) => r.data);
  },
  getReport: (id) => api.get(`/admin/reports/${id}`).then((r) => r.data),
  // Async Report Pipeline P2 — controlled manual retry of a failed generation.
  retryGeneration: (id) => api.post(`/admin/reports/${id}/retry-generation`).then((r) => r.data),
  startReview: (id) => api.post(`/admin/reports/${id}/start-review`).then((r) => r.data),
  saveEdit: (id, content, p4Narrative) =>
    api.post(`/admin/reports/${id}/versions`, { content, ...(p4Narrative ? { p4_narrative: p4Narrative } : {}) }).then((r) => r.data),
  regenerate: (id, hint) =>
    api.post(`/admin/reports/${id}/regenerate`, { hint }).then((r) => r.data),
  approve: (id) => api.post(`/admin/reports/${id}/approve`).then((r) => r.data),
  reject: (id, reason) => api.post(`/admin/reports/${id}/reject`, { reason }).then((r) => r.data),
  release: (id, opts = {}) =>
    api.post(`/admin/reports/${id}/release`, { human_review_confirmed: !!opts.human_review_confirmed }).then((r) => r.data),
  archive: (id) => api.post(`/admin/reports/${id}/archive`).then((r) => r.data),
  // R1 — optional HM Admin quality review + archive request + discard
  requestAdminReview: (id) => api.post(`/admin/reports/${id}/request-admin-review`).then((r) => r.data),
  cancelAdminReview: (id) => api.post(`/admin/reports/${id}/cancel-admin-review`).then((r) => r.data),
  startAdminReview: (id) => api.post(`/admin/reports/${id}/start-admin-review`).then((r) => r.data),
  returnWithComments: (id, comments) => api.post(`/admin/reports/${id}/return-with-comments`, { comments }).then((r) => r.data),
  completeAdminReview: (id, comments) => api.post(`/admin/reports/${id}/complete-admin-review`, { comments: comments || null }).then((r) => r.data),
  requestReportArchive: (id, body) => api.post(`/admin/reports/${id}/request-archive`, body).then((r) => r.data),
  discardReport: (id) => api.post(`/admin/reports/${id}/discard`).then((r) => r.data),

  // Assignments
  patchAssignment: (id, body) =>
    api.patch(`/admin/assignments/${id}`, body).then((r) => r.data),
  archiveAssignment: (id) => api.post(`/admin/assignments/${id}/archive`).then((r) => r.data),
  unarchiveAssignment: (id) => api.post(`/admin/assignments/${id}/unarchive`).then((r) => r.data),
  suiteConsoleSummary: (code) => api.get(`/admin/suites/${code}/console-summary`).then((r) => r.data),
  listAssignments: (opts = {}) => {
    const params = {};
    if (opts.book === 'all') params.book = 'all';
    // Iter 41.32 — optional server-side filters
    if (opts.company_id)  params.company_id = opts.company_id;
    if (opts.suite_code)  params.suite_code = opts.suite_code;
    if (opts.status)      params.status     = opts.status;
    if (opts.assigned_by) params.assigned_by= opts.assigned_by;
    if (opts.archived)    params.archived   = opts.archived;
    if (opts.date_from)   params.date_from  = opts.date_from;
    if (opts.date_to)     params.date_to    = opts.date_to;
    return api.get('/admin/assignments', { params }).then((r) => r.data);
  },
  createAssignment: (body) => api.post('/admin/assignments', body).then((r) => r.data),

  // Suite overview (aggregated, read-only)
  suitesOverview: (params = {}) =>
    api.get('/admin/suites/overview', { params }).then((r) => r.data),

  // Talent Intelligence
  intelligenceConfig: () => api.get('/admin/intelligence/config').then((r) => r.data),
  saveIntelligenceConfig: (body) => api.put('/admin/intelligence/config', body).then((r) => r.data),
  intelligenceCompanyConfig: (companyId) =>
    api.get(`/admin/intelligence/config/${companyId}`).then((r) => r.data),
  saveIntelligenceCompanyConfig: (companyId, body) =>
    api.put(`/admin/intelligence/config/${companyId}`, body).then((r) => r.data),
  intelligenceOverview: (companyId, departmentId = null) =>
    api.get('/admin/intelligence/overview', { params: { company_id: companyId, ...(departmentId ? { department_id: departmentId } : {}) } }).then((r) => r.data),
  intelligenceEmployee: (userId) =>
    api.get(`/admin/intelligence/employee/${userId}`).then((r) => r.data),
  intelligenceNarrative: (companyId, departmentId = null) =>
    api.post('/admin/intelligence/narrative', null, { params: { company_id: companyId, ...(departmentId ? { department_id: departmentId } : {}) }, timeout: 120000 }).then((r) => r.data),
  listDepartments: (companyId, includeArchived = false) =>
    api.get('/admin/departments', { params: { company_id: companyId, include_archived: includeArchived } }).then((r) => r.data),
  createDepartment: (body) => api.post('/admin/departments', body).then((r) => r.data),
  updateDepartment: (id, body) => api.patch(`/admin/departments/${id}`, body).then((r) => r.data),
  getCompensation: (userId) => api.get(`/admin/compensation/${userId}`).then((r) => r.data),
  saveCompensation: (userId, body) => api.put(`/admin/compensation/${userId}`, body).then((r) => r.data),
  listCompanyEmployees: (companyId) =>
    api.get(`/admin/companies/${companyId}/employees`).then((r) => r.data),
  listAssignees: (companyId) =>
    api.get('/admin/intelligence/assignees', { params: { company_id: companyId } }).then((r) => r.data),

  // Actions (decision log)
  listActions: (companyId, params = {}) =>
    api.get('/admin/intelligence/actions', { params: { company_id: companyId, ...params } }).then((r) => r.data),
  createAction: (companyId, body) =>
    api.post('/admin/intelligence/actions', body, { params: { company_id: companyId } }).then((r) => r.data),
  updateAction: (companyId, actionId, body) =>
    api.patch(`/admin/intelligence/actions/${actionId}`, body, { params: { company_id: companyId } }).then((r) => r.data),

  // Snapshots
  listSnapshots: (companyId, departmentId = null) =>
    api.get('/admin/intelligence/snapshots', { params: { company_id: companyId, ...(departmentId ? { department_id: departmentId } : {}) } }).then((r) => r.data),
  createSnapshot: (companyId, departmentId = null, notes = null) =>
    api.post('/admin/intelligence/snapshots', null, { params: { company_id: companyId, ...(departmentId ? { department_id: departmentId } : {}), ...(notes ? { notes } : {}) } }).then((r) => r.data),

  // Export
  exportIntelCsvUrl: (companyId, departmentId = null) =>
    `${api.defaults.baseURL}/admin/intelligence/export.csv?company_id=${encodeURIComponent(companyId)}${departmentId ? `&department_id=${encodeURIComponent(departmentId)}` : ''}`,
  exportIntelPdfUrl: (companyId, departmentId = null, includeNarrative = false) =>
    `${api.defaults.baseURL}/admin/intelligence/export.pdf?company_id=${encodeURIComponent(companyId)}${departmentId ? `&department_id=${encodeURIComponent(departmentId)}` : ''}${includeNarrative ? '&include_narrative=true' : ''}`,

  // Companies (admin) — used by intelligence company selector
  listCompanies: (opts = {}) =>
    api.get('/admin/clients', {
      params: opts.book === 'all' ? { book: 'all' } : {},
    }).then((r) => r.data),

  // Role profile industries (Prompt 17)
  listRoleIndustries: () => api.get('/admin/role_profiles/industries').then((r) => r.data),

  // Client profile (Prompt 6)
  getClientProfile: (companyId) =>
    api.get(`/admin/clients/${companyId}`).then((r) => r.data),
  updateClientSuites: (companyId, suiteCodes, contractedSuites = null) =>
    api.patch(`/admin/clients/${companyId}/suites`, {
      suite_codes: suiteCodes,
      ...(contractedSuites ? { contracted_suites: contractedSuites } : {}),
    }).then((r) => r.data),
  listAllSuites: () => api.get('/admin/suites/catalog').then((r) => r.data),

  // Collaborator status (Prompt 7)
  getCollabStatus: (userId) =>
    api.get(`/admin/intelligence/collaborator-status/${userId}`).then((r) => r.data),
  updateCollabStatus: (userId, status) =>
    api.patch(`/admin/intelligence/collaborator-status/${userId}`, { status }).then((r) => r.data),

  // Evaluation detail (Prompt 10)
  getAssignment: (assignmentId) =>
    api.get(`/admin/assignments/${assignmentId}`).then((r) => r.data),
  saveFinalRecommendation: (assignmentId, text) =>
    api.patch(`/admin/assignments/${assignmentId}/final-recommendation`, { text }).then((r) => r.data),

  // Users management (Prompt 11)
  listAllUsers: () => api.get('/admin/users').then((r) => r.data),
  // Sprint Users-Nav — paginated server-side contract ({items,total,page,...}).
  listUsers: (params = {}) => api.get('/admin/users', { params }).then((r) => r.data),
  createUser: (body) => api.post('/admin/users', body).then((r) => r.data),
  resetUserPassword: (id) => api.post(`/admin/users/${id}/reset-password`).then((r) => r.data),
  forcePasswordChange: (id) => api.post(`/admin/users/${id}/force-password-change`).then((r) => r.data),

  // Climara Campaigns (Sprint CMA-B)
  climaraListCampaigns: (companyId = null) =>
    api.get('/admin/climara/campaigns', {
      params: companyId ? { company_id: companyId } : {},
    }).then((r) => r.data),
  climaraCreateCampaign: (body) =>
    api.post('/admin/climara/campaigns', body).then((r) => r.data),
  climaraGetCampaign: (id) =>
    api.get(`/admin/climara/campaigns/${id}`).then((r) => r.data),
  climaraCloseCampaign: (id) =>
    api.post(`/admin/climara/campaigns/${id}/close`).then((r) => r.data),

  // Climara Reports (Sprint CMA-D)
  climaraGenerateReport: (campaignId, locale = 'en') =>
    api.post(`/admin/climara/campaigns/${campaignId}/generate-report`, null, {
      params: { locale },
    }).then((r) => r.data),
  climaraListReports: (companyId = null) =>
    api.get('/admin/climara/reports', {
      params: companyId ? { company_id: companyId } : {},
    }).then((r) => r.data),
  climaraGetReport: (id) =>
    api.get(`/admin/climara/reports/${id}`).then((r) => r.data),
  climaraReleaseReport: (id) =>
    api.post(`/admin/climara/reports/${id}/release`).then((r) => r.data),
  climaraSaveInterpretation: (id, text) =>
    api.put(`/admin/climara/reports/${id}/interpretation`, { text }).then((r) => r.data),
  climaraPublishInterpretation: (id, text, acknowledgeStale = false) =>
    api.post(`/admin/climara/reports/${id}/interpretation/publish`, { text, acknowledge_stale: acknowledgeStale }).then((r) => r.data),
  climaraAiInterpretation: (id, locale = 'es') =>
    api.post(`/admin/climara/reports/${id}/interpretation/ai`, null, { params: { locale } }).then((r) => r.data),
  climaraExportReportUrl: (id, ext) =>
    `${api.defaults.baseURL}/admin/climara/reports/${id}/export.${ext}`,
  // Wave B — campaign requests + team building
  climaraCampaignRequests: (status = null) =>
    api.get('/admin/climara/campaign-requests', { params: status ? { status } : {} }).then((r) => r.data),
  climaraApproveCampaignRequest: (id, questionnaireId, note) =>
    api.post(`/admin/climara/campaign-requests/${id}/approve`, { questionnaire_id: questionnaireId, note }).then((r) => r.data),
  climaraRejectCampaignRequest: (id, reason) =>
    api.post(`/admin/climara/campaign-requests/${id}/reject`, { reason }).then((r) => r.data),
  climaraGenerateTeamBuilding: (reportId, locale = 'es') =>
    api.post(`/admin/climara/reports/${reportId}/team-building/generate`, null, { params: { locale }, timeout: 60000 }).then((r) => r.data),
  climaraTeamBuildingList: (companyId = null) =>
    api.get('/admin/climara/team-building', { params: companyId ? { company_id: companyId } : {} }).then((r) => r.data),
  climaraPatchTeamBuilding: (id, body) =>
    api.patch(`/admin/climara/team-building/${id}`, body).then((r) => r.data),
  climaraProposeTeamBuilding: (id) =>
    api.post(`/admin/climara/team-building/${id}/propose`).then((r) => r.data),
  climaraGetCampaignRequest: (id) =>
    api.get(`/admin/climara/campaign-requests/${id}`).then((r) => r.data),
  climaraCampaignRequestMessage: (id, body) =>
    api.post(`/admin/climara/campaign-requests/${id}/messages`, { body }).then((r) => r.data),
  climaraRequestChanges: (id, note) =>
    api.post(`/admin/climara/campaign-requests/${id}/request-changes`, { note }).then((r) => r.data),
  climaraCampaignConsole: (id) =>
    api.get(`/admin/climara/campaigns/${id}/console`).then((r) => r.data),
  climaraRemindCampaign: (id, userIds = []) =>
    api.post(`/admin/climara/campaigns/${id}/remind`, { user_ids: userIds }).then((r) => r.data),
  climaraExtendDeadline: (id, deadline) =>
    api.post(`/admin/climara/campaigns/${id}/extend-deadline`, { deadline }).then((r) => r.data),
  climaraSupervision: () =>
    api.get('/admin/climara/supervision').then((r) => r.data),

  // TalentTrain (Prompts 20 & 21)
  ttListUsers: (cid) =>
    api.get(`/admin/talent-train/${cid}/users`).then((r) => r.data),
  ttGetUser: (cid, uid) =>
    api.get(`/admin/talent-train/${cid}/users/${uid}`).then((r) => r.data),
  ttCreateAction: (cid, uid, body) =>
    api.post(`/admin/talent-train/${cid}/users/${uid}/actions`, body).then((r) => r.data),
  ttUpdateAction: (cid, aid, patch) =>
    api.patch(`/admin/talent-train/${cid}/actions/${aid}`, patch).then((r) => r.data),
  ttDeleteAction: (cid, aid) =>
    api.delete(`/admin/talent-train/${cid}/actions/${aid}`).then((r) => r.data),
  ttSeedFromEvaluation: (cid, assignmentId) =>
    api.post(`/admin/talent-train/${cid}/seed-from-evaluation`, { assignment_id: assignmentId }).then((r) => r.data),
  ttDiscussionGet: (cid, uid) =>
    api.get(`/admin/talent-train/${cid}/users/${uid}/discussion`).then((r) => r.data),
  ttDiscussionPost: (cid, uid, text) =>
    api.post(`/admin/talent-train/${cid}/users/${uid}/discussion`, { text }).then((r) => r.data),
  ttDiscussionRead: (cid, uid) =>
    api.post(`/admin/talent-train/${cid}/users/${uid}/discussion/read`).then((r) => r.data),
  ttDiscussionNextAction: (cid, uid, text) =>
    api.put(`/admin/talent-train/${cid}/users/${uid}/discussion/next-action`, { text }).then((r) => r.data),
  ttDiscussionUnread: (cid) =>
    api.get(`/admin/talent-train/${cid}/discussion-unread`).then((r) => r.data),

  // Sprint TT-C — HM Admin TalentTrain workspace (read-only mirrors,
  // advisor-scoped on the backend). No new screens; these feed the existing
  // AdminTalentTrain page tabs.
  ttAdminOverview: (cid) =>
    api.get(`/admin/talenttrain/${cid}/overview`).then((r) => r.data),
  ttAdminListCertificates: (cid) =>
    api.get(`/admin/talenttrain/${cid}/certificates`).then((r) => r.data),
  ttAdminTrainingAlerts: (cid) =>
    api.get(`/admin/talenttrain/${cid}/alerts`).then((r) => r.data),
  ttAdminGetRoleMatrix: (cid) =>
    api.get(`/admin/talenttrain/${cid}/role-matrix`).then((r) => r.data),
  ttAdminListReports: (cid) =>
    api.get(`/admin/talenttrain/${cid}/reports`).then((r) => r.data),
  ttAdminValidateExternalCert: (cid, certId, action) =>
    api.patch(`/admin/talenttrain/${cid}/certificates/${certId}/validate`,
              { action }).then((r) => r.data),

  // Sprint TS-B — HM Admin TalentStart workspace (read-only mirrors,
  // advisor-scoped on the backend). Mirrors the TalentTrain TT-C pattern.
  tsAdminOverview: (cid) =>
    api.get(`/admin/talentstart/${cid}/overview`).then((r) => r.data),
  tsAdminListTemplates: (cid) =>
    api.get(`/admin/talentstart/${cid}/templates`).then((r) => r.data),
  tsAdminListAssignments: (cid) =>
    api.get(`/admin/talentstart/${cid}/assignments`).then((r) => r.data),
  tsAdminListReports: (cid) =>
    api.get(`/admin/talentstart/${cid}/reports`).then((r) => r.data),

  // Sprint TF-A — HM Admin TalentFix Oversight + Closure pack.
  tfAdminOversight: () =>
    api.get('/admin/talentfix/oversight').then((r) => r.data),
  tfAdminListCompanyReports: (cid) =>
    api.get(`/admin/talentfix/${cid}/reports`).then((r) => r.data),
  tfClosurePdfUrl: (caseId) =>
    `${api.defaults.baseURL}/admin/talentfix/cases/${caseId}/closure.pdf`,
  tfClosureDocxUrl: (caseId) =>
    `${api.defaults.baseURL}/admin/talentfix/cases/${caseId}/closure.docx`,
  tfClosureXlsxUrl: (caseId) =>
    `${api.defaults.baseURL}/admin/talentfix/cases/${caseId}/closure.xlsx`,
  tfMediationSummaryPdfUrl: (mid) =>
    `${api.defaults.baseURL}/admin/talentfix/mediations/${mid}/summary.pdf`,
  tfRenderClosure: async (caseId, fmt) => {
    const r = await api.post(`/admin/talentfix/cases/${caseId}/closure.${fmt}`,
                             null, { responseType: 'blob' });
    const blob = new Blob([r.data], { type: r.headers['content-type'] });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `talentfix-closure-${caseId.slice(0, 8)}.${fmt}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  // Dashboard AI alert actions (Iter 41.3)
  listAlertActions: () =>
    api.get('/admin/dashboard-alerts/actions').then((r) => r.data),
  // Founder UX Review — real operational dashboard (advisor/admin scoped)
  getOperationsDashboard: () =>
    api.get('/admin/dashboard/operations').then((r) => r.data),
  listAllAlertActions: () =>
    api.get('/admin/dashboard-alerts/actions/all').then((r) => r.data),
  recordAlertAction: (alertKey, action) =>
    api.post(`/admin/dashboard-alerts/${alertKey}/action`, { action }).then((r) => r.data),
  clearAlertAction: (alertKey) =>
    api.delete(`/admin/dashboard-alerts/${alertKey}/action`).then((r) => r.data),

  // Per-client questionnaire customization (Iter 41.6)
  forkQuestionnaireForClient: (qid, companyId) =>
    api.post(`/admin/questionnaires/${qid}/fork-for-client`, { company_id: companyId })
       .then((r) => r.data),
  getClientVersion: (qid, companyId) =>
    api.get(`/admin/questionnaires/${qid}/client-version`, { params: { company_id: companyId } })
       .then((r) => r.data),
  patchVersionQuestion: (vid, qqid, patch) =>
    api.put(`/admin/questionnaire-versions/${vid}/questions/${qqid}`, patch)
       .then((r) => r.data),
  addVersionQuestion: (vid, payload) =>
    api.post(`/admin/questionnaire-versions/${vid}/questions`, payload)
       .then((r) => r.data),
  reorderVersionQuestions: (vid, questionIds) =>
    api.put(`/admin/questionnaire-versions/${vid}/reorder`, { question_ids: questionIds })
       .then((r) => r.data),

  // Iter 41.7 — AI suggestions + custom versions list
  listClientVersions: (qid) =>
    api.get(`/admin/questionnaires/${qid}/client-versions`).then((r) => r.data),
  suggestCustomizations: (qid, companyId, locale = 'es') =>
    api.post(`/admin/questionnaires/${qid}/suggest-customizations`, { company_id: companyId, locale })
       .then((r) => r.data),
  acceptSuggestion: (sid) =>
    api.post(`/admin/questionnaire-suggestions/${sid}/accept`).then((r) => r.data),
  rejectSuggestion: (sid) =>
    api.post(`/admin/questionnaire-suggestions/${sid}/reject`).then((r) => r.data),

  // Iter 41.8 — report export from the assignment-detail page
  exportReport: (reportId, format = 'pdf') =>
    api.get(`/admin/reports/${reportId}/export/${format}`, { responseType: 'blob' })
       .then((r) => r.data),

  // Industries (BUG 02)
  listIndustries: () => api.get('/admin/industries').then((r) => r.data),
  getIndustry: (code) => api.get(`/admin/industries/${code}`).then((r) => r.data),

  // Create client (BUG 01 + BUG 02)
  createClient: (body) => api.post('/admin/clients', body).then((r) => r.data),
  // Iter 31 (R1) — update client + list consultants
  updateClient: (id, body) => api.patch(`/admin/clients/${id}`, body).then((r) => r.data),
  listConsultants: () => api.get('/admin/consultants').then((r) => r.data),

  // Iter 31 (R2) — suite documentation
  getSuiteDoc: (code) => api.get(`/suite-docs/${code}`).then((r) => r.data),
  patchSuiteDoc: (code, body) => api.patch(`/suite-docs/${code}`, body).then((r) => r.data),
  uploadSuiteDocFile: (code, slot, file) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post(`/suite-docs/${code}/upload/${slot}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },
  deleteSuiteDocFile: (code, slot) => api.delete(`/suite-docs/${code}/file/${slot}`).then((r) => r.data),
  suiteDocDownloadUrl: (code, slot) => `${api.defaults.baseURL}/suite-docs/${code}/download/${slot}`,

  // Sprint A6.4 — TalentHeart program activation workflow (admin side)
  listProgramActivationRequests: (params = {}) =>
    api.get('/admin/theart/program-activation-requests', { params }).then((r) => r.data),
  getProgramActivationRequest: (rid) =>
    api.get(`/admin/theart/program-activation-requests/${rid}`).then((r) => r.data),
  approveProgramActivation: (rid, body = {}) =>
    api.post(`/admin/theart/program-activation-requests/${rid}/approve`, body).then((r) => r.data),
  rejectProgramActivation: (rid, body) =>
    api.post(`/admin/theart/program-activation-requests/${rid}/reject`, body).then((r) => r.data),
  postProgramActivationMessage: (rid, body) =>
    api.post(`/admin/theart/program-activation-requests/${rid}/messages`, body).then((r) => r.data),

  // Sprint TH-C — TalentHeart admin workspace
  talentHeartListCompanies: () =>
    api.get('/admin/theart/companies').then((r) => r.data),
  talentHeartListReports: (params = {}) =>
    api.get('/admin/theart/reports', { params }).then((r) => r.data),
  talentHeartGenerateReport: (cid, locale = 'es') =>
    api.post(`/admin/theart/companies/${cid}/generate-report`, null, { params: { locale } }).then((r) => r.data),
  talentHeartGetReport: (rid) =>
    api.get(`/admin/theart/reports/${rid}`).then((r) => r.data),
  talentHeartAiNarrative: (rid, locale) =>
    api.post(`/admin/theart/reports/${rid}/ai-narrative`, null, { params: locale ? { locale } : {} }).then((r) => r.data),
  talentHeartReleaseReport: (rid) =>
    api.post(`/admin/theart/reports/${rid}/release`).then((r) => r.data),
  talentHeartReportPdfUrl: (rid) =>
    `${api.defaults.baseURL}/admin/theart/reports/${rid}/export.pdf`,

  // Sprint TF-1 Phase 1 — TalentFix (Stage 1 only) — admin / advisor surface
  talentFixListCases: (params = {}) =>
    api.get('/admin/talentfix/cases', { params }).then((r) => r.data),
  talentFixGetCase: (cid) =>
    api.get(`/admin/talentfix/cases/${cid}`).then((r) => r.data),
  talentFixGetTimeline: (cid) =>
    api.get(`/admin/talentfix/cases/${cid}/timeline`).then((r) => r.data),
  talentFixOpenCase: (body) =>
    api.post('/admin/talentfix/cases', body).then((r) => r.data),
  talentFixClassify: (cid, body) =>
    api.post(`/admin/talentfix/cases/${cid}/classify`, body).then((r) => r.data),
  talentFixAccept: (cid, body = {}) =>
    api.post(`/admin/talentfix/cases/${cid}/accept`, body).then((r) => r.data),
  talentFixDecline: (cid, body) =>
    api.post(`/admin/talentfix/cases/${cid}/decline`, body).then((r) => r.data),
  talentFixAuditQuery: (params = {}) =>
    api.get('/admin/talentfix/audit', { params }).then((r) => r.data),
  talentFixGetDiagnosticPlan: (cid) =>
    api.get(`/admin/talentfix/cases/${cid}/diagnostic-plan`).then((r) => r.data),
  // Phase 3 — Interviews
  talentFixListInterviews: (cid) =>
    api.get(`/admin/talentfix/cases/${cid}/interviews`).then((r) => r.data),
  talentFixGetInterview: (iid) =>
    api.get(`/admin/talentfix/interviews/${iid}`).then((r) => r.data),
  talentFixScheduleInterview: (cid, body) =>
    api.post(`/admin/talentfix/cases/${cid}/interviews`, body).then((r) => r.data),
  talentFixPatchInterview: (iid, body) =>
    api.patch(`/admin/talentfix/interviews/${iid}`, body).then((r) => r.data),
  talentFixCompleteInterview: (iid, body) =>
    api.post(`/admin/talentfix/interviews/${iid}/complete`, body).then((r) => r.data),
  talentFixCancelInterview: (iid, body) =>
    api.post(`/admin/talentfix/interviews/${iid}/cancel`, body).then((r) => r.data),
  // Phase 3 — Observations
  talentFixListObservations: (cid) =>
    api.get(`/admin/talentfix/cases/${cid}/observations`).then((r) => r.data),
  talentFixGetObservation: (oid) =>
    api.get(`/admin/talentfix/observations/${oid}`).then((r) => r.data),
  talentFixPlanObservation: (cid, body) =>
    api.post(`/admin/talentfix/cases/${cid}/observations`, body).then((r) => r.data),
  talentFixPatchObservation: (oid, body) =>
    api.patch(`/admin/talentfix/observations/${oid}`, body).then((r) => r.data),
  talentFixCompleteObservation: (oid, body) =>
    api.post(`/admin/talentfix/observations/${oid}/complete`, body).then((r) => r.data),
  talentFixCancelObservation: (oid, body) =>
    api.post(`/admin/talentfix/observations/${oid}/cancel`, body).then((r) => r.data),
  // Phase 4a — Evidence Vault
  talentFixListEvidence: (cid, params = {}) =>
    api.get(`/admin/talentfix/cases/${cid}/evidence`, { params }).then((r) => r.data),
  talentFixGetEvidence: (eid) =>
    api.get(`/admin/talentfix/evidence/${eid}`).then((r) => r.data),
  talentFixGetEvidenceLineage: (eid) =>
    api.get(`/admin/talentfix/evidence/${eid}/lineage`).then((r) => r.data),
  talentFixGetEvidenceUsage: (eid) =>
    api.get(`/admin/talentfix/evidence/${eid}/usage`).then((r) => r.data),
  talentFixUploadEvidence: (cid, formData) =>
    api.post(`/admin/talentfix/cases/${cid}/evidence`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data),
  talentFixCaptureSnapshot: (cid, body) =>
    api.post(`/admin/talentfix/cases/${cid}/evidence/snapshot`, body).then((r) => r.data),
  talentFixCaptureCommunication: (cid, body) =>
    api.post(`/admin/talentfix/cases/${cid}/evidence/communication`, body).then((r) => r.data),
  talentFixCaptureWitness: (cid, body) =>
    api.post(`/admin/talentfix/cases/${cid}/evidence/witness-statement`, body).then((r) => r.data),
  talentFixPatchEvidence: (eid, body) =>
    api.patch(`/admin/talentfix/evidence/${eid}`, body).then((r) => r.data),
  talentFixChangeConfidentiality: (eid, body) =>
    api.post(`/admin/talentfix/evidence/${eid}/confidentiality`, body).then((r) => r.data),
  talentFixReleaseLegalHold: (eid, body) =>
    api.post(`/admin/talentfix/evidence/${eid}/legal-hold/release`, body).then((r) => r.data),
  talentFixNewVersion: (eid, formData) =>
    api.post(`/admin/talentfix/evidence/${eid}/new-version`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data),
  talentFixSoftDeleteEvidence: (eid, body) =>
    api.post(`/admin/talentfix/evidence/${eid}/soft-delete`, body).then((r) => r.data),
  talentFixLinkInterviewEvidence: (iid, body) =>
    api.post(`/admin/talentfix/interviews/${iid}/link-evidence`, body).then((r) => r.data),
  talentFixLinkObservationEvidence: (oid, body) =>
    api.post(`/admin/talentfix/observations/${oid}/link-evidence`, body).then((r) => r.data),

  // -------- (moved here from clientApi for correct RBAC binding — Feb 2026 RCA) --------
  // Iter 41.35 — Advisors (Consultor/Asesor) management (hm_admin only)
  listAdvisors: () => api.get('/admin/advisors').then((r) => r.data),
  getAdvisor:  (id) => api.get(`/admin/advisors/${id}`).then((r) => r.data),
  createAdvisor: (body) => api.post('/admin/advisors', body).then((r) => r.data),
  updateAdvisorAssignments: (id, ids) =>
    api.put(`/admin/advisors/${id}/assignments`, { assigned_client_ids: ids }).then((r) => r.data),
  patchAdvisor: (id, body) => api.patch(`/admin/advisors/${id}`, body).then((r) => r.data),
  deleteAdvisor: (id) => api.delete(`/admin/advisors/${id}`).then((r) => r.data),
  // UAT Sprint 01 — client + user CRUD used by Clients / Users list kebab menu
  deleteClient: (id) => api.delete(`/admin/clients/${id}`).then((r) => r.data),
  // Sprint Governance — reversible client archive (Archive Service)
  archiveClient: (id) => api.post(`/admin/archive/company/${id}`, { reason_code: 'manual_archive' }).then((r) => r.data),
  // Sprint Governance — config change requests
  listConfigChangeRequests: (params = {}) => api.get('/admin/config-change-requests', { params }).then((r) => r.data),
  getConfigChangeRequest: (id) => api.get(`/admin/config-change-requests/${id}`).then((r) => r.data),
  approveConfigChangeRequest: (id) => api.post(`/admin/config-change-requests/${id}/approve`).then((r) => r.data),
  rejectConfigChangeRequest: (id, reason) => api.post(`/admin/config-change-requests/${id}/reject`, { reason }).then((r) => r.data),
  refreshConfigChangeRequest: (id) => api.post(`/admin/config-change-requests/${id}/refresh`).then((r) => r.data),
  cancelConfigChangeRequest: (id) => api.post(`/admin/config-change-requests/${id}/cancel`).then((r) => r.data),
  postConfigChangeRequestMessage: (id, body) => api.post(`/admin/config-change-requests/${id}/messages`, { body }).then((r) => r.data),
  patchUser: (id, body) => api.patch(`/admin/users/${id}`, body).then((r) => r.data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`).then((r) => r.data),

  // Iter 41.36 — Commercial CRM (hm_admin + hm_advisor)
  listCommercialLeads: (params = {}) => api.get('/admin/commercial/leads', { params }).then((r) => r.data),
  getCommercialLead:   (id) => api.get(`/admin/commercial/leads/${id}`).then((r) => r.data),
  createCommercialLead: (body) => api.post('/admin/commercial/leads', body).then((r) => r.data),
  patchCommercialLead:  (id, body) => api.patch(`/admin/commercial/leads/${id}`, body).then((r) => r.data),
  deleteCommercialLead: (id) => api.delete(`/admin/commercial/leads/${id}`).then((r) => r.data),
  addCommercialActivity: (id, body) =>
    api.post(`/admin/commercial/leads/${id}/activities`, body).then((r) => r.data),
  commercialMetrics: () => api.get('/admin/commercial/metrics').then((r) => r.data),

  // Org Structure Board (Founder V3.1)
  getOrgBoard: (companyId) => api.get(`/admin/org-board/${companyId}`).then((r) => r.data),

  // Renewal Reminders (in-app only — no billing mutations)
  listRenewalReminders: () => api.get('/admin/renewal-reminders').then((r) => r.data),
  ackRenewalReminder: (subId, window) =>
    api.post(`/admin/renewal-reminders/${subId}/ack`, { window }).then((r) => r.data),

  // Global Archive (reusable archive framework)
  archiveEntity: (type, id, body = {}) =>
    api.post(`/admin/archive/${type}/${id}`, body).then((r) => r.data),
  restoreEntity: (type, id) =>
    api.post(`/admin/trash/${type}/${id}/restore`).then((r) => r.data),

  // Iter 41.37 — CRM lead → existing client-creation / suite-activation request wrappers
  requestClientCreationFromLead: (leadId, body = {}) =>
    api.post(`/admin/commercial/leads/${leadId}/request-client-creation`, body).then((r) => r.data),
  requestSuiteActivationFromLead: (leadId, body) =>
    api.post(`/admin/commercial/leads/${leadId}/request-suite-activation`, body).then((r) => r.data),
  commercialExecutiveDashboard: () =>
    api.get('/admin/commercial/executive-dashboard').then((r) => r.data),

  // Iter 41.38 — Lightweight Commercial Billing & Subscriptions
  getBillingProfile:   (cid)             => api.get(`/admin/commercial/billing/clients/${cid}`).then((r) => r.data),
  updateBillingProfile:(cid, body)       => api.put(`/admin/commercial/billing/clients/${cid}`, body).then((r) => r.data),
  listSubscriptions:   (cid)             => api.get(`/admin/commercial/billing/clients/${cid}/subscriptions`).then((r) => r.data),
  createSubscription:  (cid, body)       => api.post(`/admin/commercial/billing/clients/${cid}/subscriptions`, body).then((r) => r.data),
  bulkAddSubscriptions:(cid, body)       => api.post(`/admin/commercial/billing/clients/${cid}/subscriptions/bulk-add`, body).then((r) => r.data),
  patchSubscription:   (subId, body)     => api.patch(`/admin/commercial/billing/subscriptions/${subId}`, body).then((r) => r.data),
  deleteSubscription:  (subId)           => api.delete(`/admin/commercial/billing/subscriptions/${subId}`).then((r) => r.data),
  getStatement:        (cid)             => api.get(`/admin/commercial/billing/clients/${cid}/statement`).then((r) => r.data),
  createManualEntry:   (cid, body)       => api.post(`/admin/commercial/billing/clients/${cid}/entries`, body).then((r) => r.data),
  listPayments:        (cid)             => api.get(`/admin/commercial/billing/clients/${cid}/payments`).then((r) => r.data),
  registerPayment:     (cid, body)       => api.post(`/admin/commercial/billing/clients/${cid}/payments`, body).then((r) => r.data),
  upsellOpportunities: (cid)             => api.get(`/admin/commercial/billing/clients/${cid}/upsell`).then((r) => r.data),
  billingMetrics:      ()                => api.get('/admin/commercial/billing/metrics').then((r) => r.data),
  billingRenewals:     ()                => api.get('/admin/commercial/billing/renewals').then((r) => r.data),
  statementPdfUrl:     (cid, lang = 'es')=> `/admin/commercial/billing/clients/${cid}/statement.pdf?lang=${lang}`,

  // ---------- TF-2 Phase 5 — Mediation ----------
  tfListMediations:        (cid)              => api.get(`/admin/talentfix/cases/${cid}/mediations`).then((r) => r.data),
  tfScheduleMediation:     (cid, body)        => api.post(`/admin/talentfix/cases/${cid}/mediations`, body).then((r) => r.data),
  tfReadMediation:         (mid)              => api.get(`/admin/talentfix/mediations/${mid}`).then((r) => r.data),
  tfPatchMediation:        (mid, body)        => api.patch(`/admin/talentfix/mediations/${mid}`, body).then((r) => r.data),
  tfCompleteMediation:     (mid, body)        => api.post(`/admin/talentfix/mediations/${mid}/complete`, body).then((r) => r.data),
  tfCancelMediation:       (mid, body)        => api.post(`/admin/talentfix/mediations/${mid}/cancel`, body).then((r) => r.data),
  tfAddCommitment:         (mid, body)        => api.post(`/admin/talentfix/mediations/${mid}/commitments`, body).then((r) => r.data),
  tfPatchCommitment:       (mid, ccid, body)  => api.patch(`/admin/talentfix/mediations/${mid}/commitments/${ccid}`, body).then((r) => r.data),
  tfDeleteCommitment:      (mid, ccid)        => api.delete(`/admin/talentfix/mediations/${mid}/commitments/${ccid}`).then((r) => r.data),
  tfVerifyCompliance:      (mid, body)        => api.post(`/admin/talentfix/mediations/${mid}/verify-compliance`, body).then((r) => r.data),
  tfLinkMediationEvidence: (mid, body)        => api.post(`/admin/talentfix/mediations/${mid}/link-evidence`, body).then((r) => r.data),
  tfPublishMediationSummary:(mid, body)       => api.post(`/admin/talentfix/mediations/${mid}/publish-summary`, body).then((r) => r.data),
  tfReopenMediation:        (mid, body)       => api.post(`/admin/talentfix/mediations/${mid}/reopen`, body).then((r) => r.data),

  // ---------- TF-2 Phase 6 — Sufficiency + Stage transitions ----------
  tf6ListRules:        (params = {})               => api.get('/admin/talentfix/sufficiency-rules', { params }).then((r) => r.data),
  tf6CreateRule:       (body)                       => api.post('/admin/talentfix/sufficiency-rules', body).then((r) => r.data),
  tf6PatchRule:        (rid, body)                  => api.patch(`/admin/talentfix/sufficiency-rules/${rid}`, body).then((r) => r.data),
  tf6DeleteRule:       (rid)                        => api.delete(`/admin/talentfix/sufficiency-rules/${rid}`).then((r) => r.data),
  tf6SeedDefaults:     ()                           => api.post('/admin/talentfix/sufficiency-rules/seed-defaults').then((r) => r.data),
  tf6GetSufficiency:   (cid, stage_target = 'findings') => api.get(`/admin/talentfix/cases/${cid}/sufficiency`, { params: { stage_target } }).then((r) => r.data),
  tf6RefreshSufficiency:(cid, stage_target = 'findings')=> api.post(`/admin/talentfix/cases/${cid}/sufficiency/refresh`, null, { params: { stage_target } }).then((r) => r.data),
  tf6RequestTransition:(cid, body)                  => api.post(`/admin/talentfix/cases/${cid}/stage-transition/request`, body).then((r) => r.data),
  tf6ApproveTransition:(cid, body)                  => api.post(`/admin/talentfix/cases/${cid}/stage-transition/approve`, body).then((r) => r.data),
  tf6DenyTransition:   (cid, body)                  => api.post(`/admin/talentfix/cases/${cid}/stage-transition/deny`, body).then((r) => r.data),
  tf6ForceTransition:  (cid, body)                  => api.post(`/admin/talentfix/cases/${cid}/stage-transition/force`, body).then((r) => r.data),
  tf6ReopenCase:       (cid, body)                  => api.post(`/admin/talentfix/cases/${cid}/stage-transition/reopen`, body).then((r) => r.data),
  tf6StageHistory:     (cid)                        => api.get(`/admin/talentfix/cases/${cid}/stage-history`).then((r) => r.data),

  // Phase 1 — Assessment Studio
  studioMeta: () => api.get('/admin/studio/meta').then((r) => r.data),
  studioLibrary: (params = {}) =>
    api.get('/admin/studio/library', { params }).then((r) => r.data),
  studioCreateAssessment: (payload) =>
    api.post('/admin/studio/assessments', payload).then((r) => r.data),
  studioTransitionAssessment: (id, target, extras = {}) =>
    api.post(`/admin/studio/assessments/${id}/transition`, { target, ...extras }).then((r) => r.data),
  // Phase 2B — autosave + history
  studioAutosave: (id, payload) =>
    api.post(`/admin/studio/assessments/${id}/autosave`, payload).then((r) => r.data),
  studioSaveHistory: (id, limit = 25) =>
    api.get(`/admin/studio/assessments/${id}/save-history`, { params: { limit } }).then((r) => r.data),
  // Phase 2C.1 — validation
  studioValidate: (id) =>
    api.post(`/admin/studio/assessments/${id}/validate`).then((r) => r.data),
  // Phase 2C.2 — versioning + diff + restore
  studioListVersions: (id, params = {}) =>
    api.get(`/admin/studio/assessments/${id}/versions`, { params }).then((r) => r.data),
  studioCreateSnapshot: (id, body) =>
    api.post(`/admin/studio/assessments/${id}/snapshot`, body || {}).then((r) => r.data),
  studioUpdateSnapshot: (id, sid, body) =>
    api.patch(`/admin/studio/assessments/${id}/snapshots/${sid}`, body).then((r) => r.data),
  studioDeleteSnapshot: (id, sid, force = false) =>
    api.delete(`/admin/studio/assessments/${id}/snapshots/${sid}`, { params: { force } }).then((r) => r.data),
  studioDiff: (id, body) =>
    api.post(`/admin/studio/assessments/${id}/diff`, body).then((r) => r.data),
  studioRestore: (id, snapshotId) =>
    api.post(`/admin/studio/assessments/${id}/restore`, null, { params: { snapshot_id: snapshotId } }).then((r) => r.data),
  // Phase 2C.4 — publish workflow
  studioPublishReadiness: (id) =>
    api.post(`/admin/studio/assessments/${id}/publish-readiness`).then((r) => r.data),
  studioPublish: (id, body) =>
    api.post(`/admin/studio/assessments/${id}/publish`, body || {}).then((r) => r.data),
};

// ---------- Client endpoints ----------

export const clientApi = {
  listReports: () => api.get('/client/reports').then((r) => r.data),
  getReport: (id) => api.get(`/client/reports/${id}`).then((r) => r.data),
  resolveCollaborators: (ids) => api.get('/client/collaborators/resolve', { params: { ids: ids.join(',') } }).then((r) => r.data),

  // Phase 4a — Evidence Vault (client-facing)
  tfListEvidence: (cid) =>
    api.get(`/client/talentfix/cases/${cid}/evidence`).then((r) => r.data),
  tfRecentlySharedEvidence: (cid) =>
    api.get(`/client/talentfix/cases/${cid}/evidence/recently-shared`).then((r) => r.data),
  tfMarkEvidenceSeen: (eid) =>
    api.post(`/client/talentfix/evidence/${eid}/mark-seen`).then((r) => r.data),

  // Phase 5 — Mediation (read-only client surface)
  tfListMediations: (cid) =>
    api.get(`/client/talentfix/cases/${cid}/mediations`).then((r) => r.data),
  tfListMediationCommitments: (cid) =>
    api.get(`/client/talentfix/cases/${cid}/mediation-commitments`).then((r) => r.data),

  // Phase 6 — Stage badge + history (client-side projection)
  tf6ClientStage:        (cid) => api.get(`/client/talentfix/cases/${cid}/stage`).then((r) => r.data),
  tf6ClientStageHistory: (cid) => api.get(`/client/talentfix/cases/${cid}/stage-history`).then((r) => r.data),

  // Talent Intelligence (own company only)
  intelligenceOverview: () => api.get('/client/intelligence/overview').then((r) => r.data),
  intelligenceEmployee: (userId) =>
    api.get(`/client/intelligence/employee/${userId}`).then((r) => r.data),
  intelligenceNarrative: () => api.post('/client/intelligence/narrative', null, { timeout: 120000 }).then((r) => r.data),
  intelligenceConfig: () => api.get('/client/intelligence/config').then((r) => r.data),
  saveIntelligenceConfig: (body) => api.put('/client/intelligence/config', body).then((r) => r.data),
  listDepartments: (includeArchived = false) =>
    api.get('/client/departments', { params: { include_archived: includeArchived } }).then((r) => r.data),
  createDepartment: (body) => api.post('/client/departments', body).then((r) => r.data),
  updateDepartment: (id, body) => api.patch(`/client/departments/${id}`, body).then((r) => r.data),
  getCompensation: (userId) => api.get(`/client/compensation/${userId}`).then((r) => r.data),
  saveCompensation: (userId, body) => api.put(`/client/compensation/${userId}`, body).then((r) => r.data),

  // TalentTrain (Prompts 20 & 21)
  ttListUsers: () => api.get('/client/talent-train/users').then((r) => r.data),
  ttGetUser: (uid) => api.get(`/client/talent-train/users/${uid}`).then((r) => r.data),
  ttCreateAction: (uid, body) =>
    api.post(`/client/talent-train/users/${uid}/actions`, body).then((r) => r.data),
  ttUpdateAction: (aid, patch) =>
    api.patch(`/client/talent-train/actions/${aid}`, patch).then((r) => r.data),
  ttDeleteAction: (aid) =>
    api.delete(`/client/talent-train/actions/${aid}`).then((r) => r.data),
  ttDiscussionGet: (uid) =>
    api.get(`/client/talent-train/users/${uid}/discussion`).then((r) => r.data),
  ttDiscussionPost: (uid, text) =>
    api.post(`/client/talent-train/users/${uid}/discussion`, { text }).then((r) => r.data),
  ttDiscussionRead: (uid) =>
    api.post(`/client/talent-train/users/${uid}/discussion/read`).then((r) => r.data),
  ttDiscussionUnread: () =>
    api.get('/client/talent-train/discussion-unread').then((r) => r.data),

  // Iter 41.15 (Sesión E / FIX 2) — evaluation requests (client portal)
  listCollaborators: () => api.get('/client/users').then((r) => r.data),
  getMyCompany: () => api.get('/client/me/company').then((r) => r.data).catch(() => null),
  createEvaluationRequest: (body) =>
    api.post('/client/evaluation-requests', body).then((r) => r.data),
  requestCancellation: (id, reason) =>
    api.post(`/client/evaluation-requests/${id}/request-cancellation`, { reason }).then((r) => r.data),
  resolveSelfService: (id) =>
    api.post(`/client/evaluation-requests/${id}/resolve-self-service`).then((r) => r.data),
  uploadAttachment: (file, scope = 'evidence') => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/client/attachments?scope=${scope}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },
  listEvaluationRequests: (status = null) =>
    api.get('/client/evaluation-requests', { params: status ? { status } : {} }).then((r) => r.data),
  getEvaluationRequest: (id) =>
    api.get(`/client/evaluation-requests/${id}`).then((r) => r.data),
  postEvaluationRequestMessage: (id, body) =>
    api.post(`/client/evaluation-requests/${id}/messages`, { body }).then((r) => r.data),

  // Sesión F (ADD 3) — decisions-unread badge
  unreadDecisionsCount: () =>
    api.get('/client/evaluation-requests/decisions-unread-count').then((r) => r.data),
  markDecisionsSeen: () =>
    api.post('/client/evaluation-requests/mark-seen').then((r) => r.data),

  // Sesión F (ADD 1) — client suite-requests
  createSuiteRequest: (body) =>
    api.post('/client/suite-requests', body).then((r) => r.data),
  listSuiteRequests: () =>
    api.get('/client/suite-requests').then((r) => r.data),

  // Sesión J-C — TalentTrain v2 (certs, role matrix, alerts)
  ttListCertificates: () =>
    api.get('/client/talenttrain/certificates').then((r) => r.data),
  ttListMyCertificates: () =>
    api.get('/me/talenttrain/certificates').then((r) => r.data),
  ttCreateInternalCert: (form) =>
    api.post('/client/talenttrain/certificates/internal', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data),
  ttUploadExternalCert: (form) =>
    api.post('/client/talenttrain/certificates/external', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data),
  ttValidateExternalCert: (id, action) =>
    api.patch(`/client/talenttrain/certificates/${id}/validate`, { action }).then((r) => r.data),
  ttGetRoleMatrix: () =>
    api.get('/client/talenttrain/role-matrix').then((r) => r.data),
  ttSetRoleMatrix: (position, body) =>
    api.put(`/client/talenttrain/role-matrix/${encodeURIComponent(position)}`, body).then((r) => r.data),
  ttTrainingAlerts: () =>
    api.get('/client/talenttrain/alerts').then((r) => r.data),
  ttCertPdfUrl: (id) => `${api.defaults.baseURL}/client/talenttrain/certificates/${id}/pdf`,
  // Sprint TT-B — company training plan PDF (unmocks the "Export Plan" button)
  ttPlanPdfUrl: (locale) => `${api.defaults.baseURL}/client/talenttrain/plan.pdf${locale ? `?locale=${encodeURIComponent(locale)}` : ''}`,
  // Sprint TS-B — graduation PDF for a completed onboarding assignment.
  tsGraduationPdfUrl: (aid) =>
    `${api.defaults.baseURL}/client/talentstart/assignments/${aid}/graduation.pdf`,

  // Sesión J-D — TalentHeart recommended-program activation requests
  activateProgram: (body) =>
    api.post('/client/theart/program-activation-requests', body).then((r) => r.data),
  listProgramActivationRequests: () =>
    api.get('/client/theart/program-activation-requests').then((r) => r.data),

  // Sprint A6.4 — TalentHeart program activation workflow (client side)
  getProgramActivationRequest: (rid) =>
    api.get(`/client/theart/program-activation-requests/${rid}`).then((r) => r.data),
  postProgramActivationMessage: (rid, body) =>
    api.post(`/client/theart/program-activation-requests/${rid}/messages`, body).then((r) => r.data),

  // Sprint TF-1 Phase 1 — TalentFix (Stage 1 / Case Request)
  talentFixListCases: (params = {}) =>
    api.get('/client/talentfix/cases', { params }).then((r) => r.data),
  talentFixGetCase: (cid) =>
    api.get(`/client/talentfix/cases/${cid}`).then((r) => r.data),
  talentFixOpenCase: (body) =>
    api.post('/client/talentfix/cases', body).then((r) => r.data),

  // Iter 41.32 — real birthdays from voluntary personal_profiles
  listTalentHeartBirthdays: (months = 1) =>
    api.get('/client/theart/birthdays', { params: { months } }).then((r) => r.data),

  // Sprint Q1.1 — real interests aggregated from voluntary personal_profiles
  listTalentHeartInterests: () =>
    api.get('/client/theart/interests').then((r) => r.data),

  // Sprint A6.1 — Peer Recognition Module
  listRecognitions: (params = {}) =>
    api.get('/client/theart/recognitions', { params }).then((r) => r.data),
  recognitionStats: () =>
    api.get('/client/theart/recognitions/stats').then((r) => r.data),
  recognitionLeaderboard: (period = 'quarter', limit = 5) =>
    api.get('/client/theart/recognitions/leaderboard', { params: { period, limit } }).then((r) => r.data),
  // Sprint TH-C — real "users without recognition in N days" (replaces the
  // previous "collabs minus top-5 leaderboard" derivation in the client UI).
  recognitionSuggestions: (sinceDays = 60, limit = 10) =>
    api.get('/client/theart/recognition-suggestions', { params: { since_days: sinceDays, limit } }).then((r) => r.data),
  createRecognition: (payload) =>
    api.post('/client/theart/recognitions', payload).then((r) => r.data),
  deleteRecognition: (rid) =>
    api.delete(`/client/theart/recognitions/${rid}`).then((r) => r.data),
  myRecognitions: (direction = 'all', limit = 50) =>
    api.get('/me/theart/recognitions', { params: { direction, limit } }).then((r) => r.data),
  // Founder UAT 2026-06 — company-wide PUBLIC recognition feed (Mi Portal).
  myRecognitionFeed: (limit = 20) =>
    api.get('/me/theart/recognitions/feed', { params: { limit } }).then((r) => r.data),
  // Founder UAT 2026-06 — birthday calendar month navigation (consent-aware).
  listTalentHeartBirthdaysByMonth: (month) =>
    api.get('/client/theart/birthdays', { params: { month } }).then((r) => r.data),
  // Founder UAT 2026-06 — real tenant activity feed for the executive dashboard.
  recentActivity: (limit = 15) =>
    api.get('/client/recent-activity', { params: { limit } }).then((r) => r.data),

  // Sprint A6.2 — Wellbeing Surveys
  listWellbeingSurveys: (status) =>
    api.get('/client/theart/wellbeing-surveys', { params: status ? { status } : {} }).then((r) => r.data),
  // Sprint TH-B — eligible published TalentHeart questionnaires for the picker
  listWellbeingQuestionnaires: () =>
    api.get('/client/theart/wellbeing-questionnaires').then((r) => r.data),
  getWellbeingSurvey: (sid) =>
    api.get(`/client/theart/wellbeing-surveys/${sid}`).then((r) => r.data),
  createWellbeingSurvey: (payload) =>
    api.post('/client/theart/wellbeing-surveys', payload).then((r) => r.data),
  openWellbeingSurvey: (sid) =>
    api.post(`/client/theart/wellbeing-surveys/${sid}/open`).then((r) => r.data),
  closeWellbeingSurvey: (sid) =>
    api.post(`/client/theart/wellbeing-surveys/${sid}/close`).then((r) => r.data),
  wellbeingKpis: () =>
    api.get('/client/theart/wellbeing-kpis').then((r) => r.data),
  // Sprint TH-C — real at-risk alerts from wellbeing aggregation.
  wellbeingAlerts: (limit = 20) =>
    api.get('/client/theart/alerts', { params: { limit } }).then((r) => r.data),
  // Evaluated user surfaces
  myWellbeingSurveys: () =>
    api.get('/me/theart/wellbeing-surveys').then((r) => r.data),
  // Msg 412 — self-serve Personal Profile Survey campaigns (no approval).
  createProfileCampaign: (payload) =>
    api.post('/client/theart/profile-campaigns', payload).then((r) => r.data),
  listProfileCampaigns: () =>
    api.get('/client/theart/profile-campaigns').then((r) => r.data),
  myProfileCampaign: () =>
    api.get('/me/theart/profile-campaign').then((r) => r.data),
  // Msg 412 — explicit Client Admin import of org positions from job titles.
  positionsImportPreview: () =>
    api.get('/client/talent-competency/positions/import-preview').then((r) => r.data),
  positionsImport: (titles) =>
    api.post('/client/talent-competency/positions/import', { titles }).then((r) => r.data),
  submitWellbeingResponse: (sid, answers) =>
    api.post(`/me/theart/wellbeing-surveys/${sid}/responses`, { answers }).then((r) => r.data),

  // Sesión J-E — TalentIntelligence strategic-analysis requests
  requestStrategicAnalysis: (body) =>
    api.post('/client/intelligence/strategic-analysis-requests', body).then((r) => r.data),
  listStrategicAnalysisRequests: () =>
    api.get('/client/intelligence/strategic-analysis-requests').then((r) => r.data),

  // Sprint A2 — TalentStart onboarding (real data)
  onboardingListTemplates: (includeArchived = false) =>
    api.get('/client/onboarding/templates', { params: { include_archived: includeArchived } }).then((r) => r.data),
  onboardingCreateTemplate: (body) =>
    api.post('/client/onboarding/templates', body).then((r) => r.data),
  onboardingGetTemplate: (tid) =>
    api.get(`/client/onboarding/templates/${tid}`).then((r) => r.data),
  onboardingPatchTemplate: (tid, body) =>
    api.patch(`/client/onboarding/templates/${tid}`, body).then((r) => r.data),
  onboardingArchiveTemplate: (tid) =>
    api.delete(`/client/onboarding/templates/${tid}`).then((r) => r.data),
  onboardingAssignTemplate: (tid, body) =>
    api.post(`/client/onboarding/templates/${tid}/assign`, body).then((r) => r.data),
  onboardingListAssignments: (params = {}) =>
    api.get('/client/onboarding/assignments', { params }).then((r) => r.data),
  onboardingGetAssignment: (aid) =>
    api.get(`/client/onboarding/assignments/${aid}`).then((r) => r.data),
  onboardingPatchStage: (aid, idx, body) =>
    api.patch(`/client/onboarding/assignments/${aid}/stages/${idx}`, body).then((r) => r.data),
  onboardingArchiveAssignment: (aid, reason) =>
    api.post(`/client/onboarding/assignments/${aid}/archive`, null, { params: reason ? { reason } : {} }).then((r) => r.data),
  onboardingRestoreAssignment: (aid) =>
    api.post(`/client/onboarding/assignments/${aid}/restore`).then((r) => r.data),
  healthSnapshot: () =>
    api.get('/client/health/snapshot').then((r) => r.data),
  archiveItems: () =>
    api.get('/client/archive/items').then((r) => r.data),
  onboardingDashboard: () =>
    api.get('/client/onboarding/dashboard').then((r) => r.data),
  onboardingMyOwn: () =>
    api.get('/me/onboarding').then((r) => r.data),

  // Sprint A3 — Climara real consolidated report
  climaraConsolidated: (locale = 'en') =>
    api.get('/client/climara/consolidated', { params: { locale } }).then((r) => r.data),
  climaraPdfUrl: (locale = 'en') =>
    `${api.defaults.baseURL}/client/climara/consolidated/pdf?locale=${locale}`,
  // Wave B — Climara executive experience
  climaraReports: () =>
    api.get('/client/climara/reports').then((r) => r.data),
  climaraReport: (id) =>
    api.get(`/client/climara/reports/${id}`).then((r) => r.data),
  climaraExecutivePdfUrl: (id, locale = 'es') =>
    `${api.defaults.baseURL}/client/climara/reports/${id}/export.pdf?locale=${locale}`,
  climaraExecutiveAnalysis: (id, locale = 'es') =>
    api.get(`/client/climara/reports/${id}/executive-analysis`, { params: { locale }, timeout: 60000 }).then((r) => r.data),
  climaraWaves: () =>
    api.get('/client/climara/waves').then((r) => r.data),
  climaraCreateCampaignRequest: (payload) =>
    api.post('/client/climara/campaign-requests', payload).then((r) => r.data),
  climaraCampaignRequests: () =>
    api.get('/client/climara/campaign-requests').then((r) => r.data),
  climaraTeamBuilding: () =>
    api.get('/client/climara/team-building').then((r) => r.data),
  climaraTeamBuildingDecision: (id, decision, note) =>
    api.post(`/client/climara/team-building/${id}/decision`, { decision, note }).then((r) => r.data),
  climaraCampaigns: () =>
    api.get('/client/climara/campaigns').then((r) => r.data),
  climaraCampaignConsole: (id) =>
    api.get(`/client/climara/campaigns/${id}/console`).then((r) => r.data),
  climaraRemindCampaign: (id, userIds = []) =>
    api.post(`/client/climara/campaigns/${id}/remind`, { user_ids: userIds }).then((r) => r.data),
  climaraCloseCampaign: (id) =>
    api.post(`/client/climara/campaigns/${id}/close`).then((r) => r.data),
  climaraCampaignRequestMessage: (id, body) =>
    api.post(`/client/climara/campaign-requests/${id}/messages`, { body }).then((r) => r.data),

  // Sprint A4 — TalentMap (reuse existing gap-analysis + intelligence overview)
  talentCompetencyGapAnalysis: () =>
    api.get('/client/talent-competency/gap-analysis').then((r) => r.data),
  intelligenceExportPdfUrl: (departmentId = null) =>
    `${api.defaults.baseURL}/client/intelligence/export.pdf${departmentId ? `?department_id=${departmentId}` : ''}`,

  // Sprint A6.3 — TalentHeart executive PDF report
  talentHeartReportPdfUrl: (locale) =>
    `${api.defaults.baseURL}/client/theart/report/pdf${locale ? `?locale=${locale}` : ''}`,
};

// ---------- Status helpers ----------

export const REPORT_STATUS_LABEL = {
  processing: 'Generating',
  generation_failed: 'Generation failed',
  draft_ai_generated: 'Draft (AI)',
  under_review: 'Under review',
  edited_by_hm: 'Edited by HM',
  pending_admin_review: 'Pending HM Admin review',
  approved: 'Approved',
  released_to_client: 'Delivered to client',
  rejected: 'Rejected',
  archived: 'Archived',
  discarded: 'Discarded',
};

export const REPORT_STATUS_TONE = {
  processing:         { dot: 'bg-info animate-pulse', text: 'text-info', bg: 'bg-info/10', border: 'border-info/20' },
  generation_failed:  { dot: 'bg-destructive', text: 'text-destructive', bg: 'bg-destructive/10', border: 'border-destructive/20' },
  draft_ai_generated: { dot: 'bg-info', text: 'text-info', bg: 'bg-info/10', border: 'border-info/20' },
  under_review:       { dot: 'bg-warning', text: 'text-warning', bg: 'bg-warning/10', border: 'border-warning/20' },
  edited_by_hm:       { dot: 'bg-primary', text: 'text-primary', bg: 'bg-primary/10', border: 'border-primary/20' },
  pending_admin_review: { dot: 'bg-accent', text: 'text-accent', bg: 'bg-accent/10', border: 'border-accent/20' },
  approved:           { dot: 'bg-success', text: 'text-success', bg: 'bg-success/10', border: 'border-success/20' },
  released_to_client: { dot: 'bg-accent', text: 'text-accent', bg: 'bg-accent/10', border: 'border-accent/20' },
  rejected:           { dot: 'bg-destructive', text: 'text-destructive', bg: 'bg-destructive/10', border: 'border-destructive/20' },
  archived:           { dot: 'bg-muted-foreground', text: 'text-muted-foreground', bg: 'bg-muted', border: 'border-border' },
  discarded:          { dot: 'bg-muted-foreground', text: 'text-muted-foreground', bg: 'bg-muted', border: 'border-border' },
};

export const FIT_VERDICT_LABEL = {
  suitable: 'Suitable',
  suitable_with_reservations: 'Suitable with reservations',
  not_suitable: 'Not suitable',
};

export const GAP_STATUS_TONE = {
  above:          { text: 'text-success', bg: 'bg-success/10' },
  on_target:      { text: 'text-info', bg: 'bg-info/10' },
  below:          { text: 'text-warning', bg: 'bg-warning/10' },
  critical_below: { text: 'text-destructive', bg: 'bg-destructive/10' },
};

// ---------- Sesión B (Iter 41.11) — approval workflows ----------

export const requestsApi = {
  listAll: (params = {}) => api.get('/admin/requests', { params }).then((r) => r.data),

  // WF1 — client creation
  createClientRequest: (body) => api.post('/admin/client-creation-requests', body).then((r) => r.data),
  listClientRequests: (status = null) =>
    api.get('/admin/client-creation-requests', { params: status ? { status } : {} }).then((r) => r.data),
  getClientRequest: (id) => api.get(`/admin/client-creation-requests/${id}`).then((r) => r.data),
  approveClientRequest: (id, body = {}) =>
    api.post(`/admin/client-creation-requests/${id}/approve`, body).then((r) => r.data),
  rejectClientRequest: (id, reason) =>
    api.post(`/admin/client-creation-requests/${id}/reject`, { reason }).then((r) => r.data),
  postClientRequestMessage: (id, body) =>
    api.post(`/admin/client-creation-requests/${id}/messages`, { body }).then((r) => r.data),

  // WF2 — suite activation
  createSuiteRequest: (body) => api.post('/admin/suite-activation-requests', body).then((r) => r.data),
  listSuiteRequests: (status = null) =>
    api.get('/admin/suite-activation-requests', { params: status ? { status } : {} }).then((r) => r.data),
  getSuiteRequest: (id) => api.get(`/admin/suite-activation-requests/${id}`).then((r) => r.data),
  approveSuiteRequest: (id) =>
    api.post(`/admin/suite-activation-requests/${id}/approve`).then((r) => r.data),
  rejectSuiteRequest: (id, reason) =>
    api.post(`/admin/suite-activation-requests/${id}/reject`, { reason }).then((r) => r.data),
  postSuiteRequestMessage: (id, body) =>
    api.post(`/admin/suite-activation-requests/${id}/messages`, { body }).then((r) => r.data),

  // WF4 — leads
  patchLead: (id, body) => api.patch(`/admin/leads/${id}`, body).then((r) => r.data),
  postLeadMessage: (id, body) =>
    api.post(`/admin/leads/${id}/messages`, { body }).then((r) => r.data),
  listConsultants: () => api.get('/admin/consultants').then((r) => r.data),

  // WF5 (Sesión E / FIX 2) — client-initiated evaluation requests
  listEvaluationRequests: (status = null) =>
    api.get('/admin/evaluation-requests', { params: status ? { status } : {} }).then((r) => r.data),
  getEvaluationRequest: (id) =>
    api.get(`/admin/evaluation-requests/${id}`).then((r) => r.data),
  approveEvaluationRequest: (id) =>
    api.post(`/admin/evaluation-requests/${id}/approve`).then((r) => r.data),
  cancellationDecision: (id, approve, note = null) =>
    api.post(`/admin/evaluation-requests/${id}/cancellation-decision`, { approve, note }).then((r) => r.data),
  rejectEvaluationRequest: (id, reason) =>
    api.post(`/admin/evaluation-requests/${id}/reject`, { reason }).then((r) => r.data),
  postEvaluationRequestMessage: (id, body) =>
    api.post(`/admin/evaluation-requests/${id}/messages`, { body }).then((r) => r.data),
  ensureEvaluationRequestDraft: (id) =>
    api.post(`/admin/evaluation-requests/${id}/ensure-draft`).then((r) => r.data),
  // Founder UAT — strategic analysis requests (Climara / TalentIntelligence)
  getStrategicRequest: (id) =>
    api.get(`/admin/intelligence/strategic-analysis-requests/${id}`).then((r) => r.data),
  approveStrategicRequest: (id) =>
    api.post(`/admin/intelligence/strategic-analysis-requests/${id}/approve`, {}).then((r) => r.data),
  rejectStrategicRequest: (id, reason) =>
    api.post(`/admin/intelligence/strategic-analysis-requests/${id}/reject`, { reason }).then((r) => r.data),
  postStrategicRequestMessage: (id, body) =>
    api.post(`/admin/intelligence/strategic-analysis-requests/${id}/messages`, { body }).then((r) => r.data),
};
